import { useFormHistoryQuery } from "app/services/api/formsApi";
import { FormEnums } from "app/services/api/types/forms/enums/FormEnums";
import { SubmittedFormModel } from "app/services/api/types/forms/models/SubmittedFormModel";
import { RootState } from "app/services/store";
import { selectResidentFormsByFilter } from "app/store/slices/formEntries";
import { selectIsOnline } from "app/store/slices/network";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import usePagination from "ui/molecules/pagination/usePagination";

interface Props {
  tag?: FormEnums;
  residentId?: number;
  formId?: number;
}

export function useFormHistoryWithOffline({ tag, residentId, formId }: Props) {
  const { currentPage, handlePageChange } = usePagination();
  const isOnline = useSelector(selectIsOnline);
  const offlineData = useSelector((state: RootState) =>
    selectResidentFormsByFilter(state, residentId, tag),
  );
  const {
    data: onlineData,
    isLoading,
    isFetching,
    isError,
  } = useFormHistoryQuery(
    {
      filter: {
        form_tag: tag,
        form_id: formId,
        resident_id: residentId,
      },
      page: currentPage,
    },
    { skip: !residentId },
  );

  const filteredOfflineData = useMemo(() => {
    if (!offlineData) return offlineData;
    if (formId) {
      return offlineData.filter((entry: SubmittedFormModel) => entry.form.id === formId);
    }
    return offlineData;
  }, [offlineData, formId]);

  const data = useMemo(() => {
    if (isFetching || (isOnline && onlineData)) return onlineData?.data;
    if (isError && filteredOfflineData) return filteredOfflineData;
    return onlineData?.data;
  }, [onlineData, filteredOfflineData, isOnline, isFetching, isError]);

  return {
    isLoading,
    handlePageChange,
    meta: onlineData?.meta,
    data,
  };
}
